import { GroupByOption } from "@design-system/ViewTable/grouping/groupByTriggerLabel";
import { GroupDescriptor } from "@design-system/ViewTable/grouping/groupByRowTypes";
import {
    ReportingCatalogItem,
    reportingCatalogReportGroup,
    reportingScopeKey,
} from "./reportingCatalog";

export type ReportingGroupBy = "entity" | "report";
export type ReportingSort = "default" | "asc" | "desc";

export const REPORTING_COLUMN_SORT = {
    asc: "asc",
    desc: "desc",
    defaultOrder: "asc",
} satisfies {
    asc: ReportingSort;
    desc: ReportingSort;
    defaultOrder: "asc" | "desc";
};

export const REPORTING_GROUP_BY_OPTIONS: Array<
    GroupByOption<ReportingGroupBy>
> = [
    { key: "entity", label: "Entity" },
    { key: "report", label: "Report" },
];

export interface OrganizedReportingCatalog {
    kind: "grouped";
    groups: GroupDescriptor[];
    childrenByKey: Record<string, ReportingCatalogItem[]>;
}

type ReportingComparator = (
    first: ReportingCatalogItem,
    second: ReportingCatalogItem,
) => number;

const compareReportLabelsAscending: ReportingComparator = (first, second) =>
    first.label.localeCompare(second.label);

const compareReportLabelsDescending: ReportingComparator = (first, second) =>
    second.label.localeCompare(first.label);

const compareScopeLabelsAscending: ReportingComparator = (first, second) => {
    if (first.scope.kind !== second.scope.kind) {
        return first.scope.kind === "consolidationGroup" ? -1 : 1;
    }
    return first.scope.label.localeCompare(second.scope.label);
};

const compareScopeLabelsDescending: ReportingComparator = (first, second) => {
    if (first.scope.kind !== second.scope.kind) {
        return first.scope.kind === "consolidationGroup" ? -1 : 1;
    }
    return second.scope.label.localeCompare(first.scope.label);
};

function getReportingComparator({
    groupBy,
    sort,
}: {
    groupBy: ReportingGroupBy;
    sort: Exclude<ReportingSort, "default">;
}): ReportingComparator {
    if (groupBy === "entity") {
        return sort === "asc"
            ? compareReportLabelsAscending
            : compareReportLabelsDescending;
    }
    return sort === "asc"
        ? compareScopeLabelsAscending
        : compareScopeLabelsDescending;
}

function sortReportingItems({
    items,
    groupBy,
    sort,
}: {
    items: readonly ReportingCatalogItem[];
    groupBy: ReportingGroupBy;
    sort: ReportingSort;
}): ReportingCatalogItem[] {
    const sortedItems = [...items];
    if (sort === "default") {
        return sortedItems;
    }
    return sortedItems.sort(getReportingComparator({ groupBy, sort }));
}

export function organizeReportingCatalog({
    items,
    groupBy,
    sort,
}: {
    items: readonly ReportingCatalogItem[];
    groupBy: ReportingGroupBy;
    sort: ReportingSort;
}): OrganizedReportingCatalog {
    const orderedKeys: string[] = [];
    const labelsByKey = new Map<string, string>();
    const unsortedChildrenByKey: Record<string, ReportingCatalogItem[]> = {};

    for (const item of items) {
        const reportGroup = reportingCatalogReportGroup(item);
        const key =
            groupBy === "entity"
                ? reportingScopeKey(item.scope)
                : reportGroup.key;
        const label =
            groupBy === "entity" ? item.scope.label : reportGroup.label;
        const existingItems = unsortedChildrenByKey[key];

        if (existingItems) {
            existingItems.push(item);
        } else {
            orderedKeys.push(key);
            labelsByKey.set(key, label);
            unsortedChildrenByKey[key] = [item];
        }
    }

    const childrenByKey: Record<string, ReportingCatalogItem[]> = {};
    const groups = orderedKeys.map((key) => {
        const groupItems = sortReportingItems({
            items: unsortedChildrenByKey[key],
            groupBy,
            sort,
        });
        childrenByKey[key] = groupItems;
        return {
            key,
            label: labelsByKey.get(key) ?? "",
            totalCount: groupItems.length,
        };
    });

    return {
        kind: "grouped",
        groups:
            groupBy === "entity"
                ? groups
                : [...groups].sort((first, second) =>
                      first.label.localeCompare(second.label),
                  ),
        childrenByKey,
    };
}
